import "./varietyThumbnail.css";

import {
  selectDedupedVisualizedVarieties,
  selectNumericalPropertiesMetricsAndAggregatedFeatures,
} from "../../../selectors/resultMap";
import { useEffect, useMemo, useState } from "react";

import { Badge } from "reactstrap";
import { BlackListWhiteList } from "../advancedFiltering/blackListWhiteList/BlackListWhiteList";
import { ComponentPlaceholder } from "../../../components/componentPlaceholder/componentPlaceholder";
import { PlotModal } from "../../../components/PlotModal/PlotModal";
import PropTypes from "prop-types";
import { PropertiesMasonry } from "../../../components/PropertiesMasonry/PropertiesMasonry";
import { useGetGroupColor } from "../../../hooks/useGetGroupColor";
import { useSelector } from "react-redux";

export const VarietyThumbnail = ({
  index,
  propertyFilter,
  getImage,
  highlightedProperty,
  setHighlightedProperty,
  horizontal,
}) => {
  const visualizedVarieties = useSelector(selectDedupedVisualizedVarieties);
  const focusedElement = visualizedVarieties[index];

  const getColor = useGetGroupColor();

  const [aggregatedFeatures] = useSelector(
    selectNumericalPropertiesMetricsAndAggregatedFeatures
  );

  // A focused element is usually a variety group, but it can also be an
  // individual plot (its displayId). A plot has no card of its own, so we
  // resolve it to its parent group and display that group instead.
  const groupEntry = useMemo(
    () =>
      aggregatedFeatures.find(({ group }) => group === focusedElement) ||
      aggregatedFeatures.find(({ features }) =>
        features.some(({ displayId }) => displayId === focusedElement)
      ),
    [aggregatedFeatures, focusedElement]
  );

  const variety = groupEntry?.group ?? focusedElement;
  // Color by the actually-focused identifier (variety name or plot displayId),
  // not the resolved group: getColor keys on focusedVarieties, and an
  // individually-focused plot lives there by its displayId, not its group.
  const color = getColor(focusedElement);
  const features = useMemo(() => groupEntry?.features ?? [], [groupEntry]);

  const [currentIndex, setCurrentIndex] = useState(0);

  // When the focused element is a specific plot, open the carousel on that
  // plot rather than the group's first one.
  useEffect(() => {
    const focusedPlotIndex = features.findIndex(
      ({ displayId }) => displayId === focusedElement
    );
    setCurrentIndex(focusedPlotIndex > 0 ? focusedPlotIndex : 0);
  }, [features, focusedElement]);

  const handlePrev = (event) => {
    event.stopPropagation();
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
    else setCurrentIndex(features.length - 1);
  };
  const handleNext = (event) => {
    event.stopPropagation();
    setCurrentIndex(currentIndex + 1);
  };
  const selectedFeature = useMemo(
    () => features[currentIndex % features.length],
    [currentIndex, features]
  );

  const [image, setImage] = useState(null);
  const [imageIsLoading, setImageIsLoading] = useState(false);

  useEffect(() => {
    // TODO: prevent refetch
    const fetchImage = async (feature) => {
      setImageIsLoading(true);
      const image = await getImage(feature, "base");
      setImageIsLoading(false);
      setImage(image);
    };

    if (selectedFeature) fetchImage(selectedFeature);
  }, [getImage, selectedFeature]);

  const [plotModalIsOpen, setPlotModalOpen] = useState(false);

  const handleClosePlotModal = () => {
    setPlotModalOpen(false);
  };

  return (
    <div
      className={`powerdash-component p-0 variety-thumbnail ${
        variety ? "" : "translucid"
      }
      `}
    >
      <PlotModal
        src={image?.url}
        isOpen={plotModalIsOpen}
        toggle={handleClosePlotModal}
      />
      {variety && (
        <>
          <div
            className="variety-thumbnail-title gap-2"
            style={{ background: color }}
          >
            <span
              // Centred, a too long name overflowed on both sides: its own
              // span keeps the start and ends with an ellipsis.
              className="text-ellipsis"
              title={variety}
            >
              {variety}
            </span>
            {features.length > 0 && <BlackListWhiteList group={variety} />}
          </div>
          {features.length > 0 ? (
            <div
              className={`variety-thumbnail-content ${
                horizontal ? "horizontal" : ""
              }`}
            >
              <div
                className={`variety-thumbnail-carousel clickable${
                  imageIsLoading ? "loading" : ""
                }`}
                style={{
                  backgroundImage: `url(${image?.url})`,
                  backgroundColor: image?.blank ? "#C3C3C3" : "black",
                }}
                onClick={() => setPlotModalOpen(true)}
              >
                {!horizontal && (
                  <div className="carousel-img-popover">
                    <div className="carousel-img-popover-header informative">
                      Plot {selectedFeature?.displayId}
                    </div>
                    <img src={image?.url} alt="" />
                  </div>
                )}
                <div className="carousel-header">
                  <span className="informative">
                    Plot {selectedFeature?.displayId}
                  </span>
                  {image && !image?.blank && (
                    <a
                      href={image.url}
                      target="_blank"
                      rel="noreferrer"
                      data-tooltip-id="tooltip"
                      data-tooltip-content="Download image"
                      data-tooltip-place="top"
                    >
                      <i className="fa-solid fa-lg fa-download" />
                    </a>
                  )}
                </div>
                {features.length > 1 && (
                  <>
                    <div className="carousel-controls">
                      <div className="carousel-control" onClick={handlePrev}>
                        <i className="fa-solid fa-lg fa-chevron-left"></i>
                      </div>
                      <div className="carousel-control" onClick={handleNext}>
                        <i className="fa-solid fa-lg fa-chevron-right"></i>
                      </div>
                    </div>
                    <Badge className="carousel-index-badge">
                      {(currentIndex % features.length) + 1} / {features.length}
                    </Badge>
                  </>
                )}
              </div>
              <PropertiesMasonry
                properties={selectedFeature.properties}
                propertyFilter={propertyFilter}
                highlightedProperty={highlightedProperty}
                setHighlightedProperty={setHighlightedProperty}
              />
            </div>
          ) : (
            <ComponentPlaceholder
              icon={"fa-solid fa-ban"}
              text="No plot for this variety in current data"
            />
          )}
        </>
      )}
    </div>
  );
};

VarietyThumbnail.propTypes = {
  index: PropTypes.number.isRequired,
  propertyFilter: PropTypes.string.isRequired,
  getImage: PropTypes.func.isRequired,
  setHighlightedProperty: PropTypes.func.isRequired,
  highlightedProperty: PropTypes.string,
  horizontal: PropTypes.bool,
};
